// src/screens/PiGuardianDetailScreen.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api from '../api';
import './PiGuardianDetailScreen.css';
import backIcon from '../assets/backspace-icon.png';

function windowLabel(w) {
  const key = String(w || '').toLowerCase();
  if (key === '7d') return 'Last 7 days';
  if (key === '30d') return 'Last 30 days';
  if (key === '60d') return 'Last 60 days';
  if (key === '90d') return 'Last 90 days';
  return 'Recent';
}

export default function PiGuardianDetailScreen() {
  const { username } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const windowParam = searchParams.get('window') || '60d';

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [row, setRow] = useState(null);

  const fetchRow = async () => {
    try {
      setLoading(true);
      setErr(null);

      // Reuse leaderboard data and pick the row for this user
      const res = await api.get('/badges/leaderboard?type=PI_GUARDIAN&limit=100');
      const items = Array.isArray(res?.data?.items)
        ? res.data.items
        : Array.isArray(res?.data)
        ? res.data
        : [];

      const found = items.find((it) => it.username === username) || null;
      setRow(found);
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username]);

  const user = useMemo(
    () => ({
      username,
      name: row?.name || username,
      profilePic: row?.profilePic || '/placeholder-profile.png',
    }),
    [row, username]
  );

  const meta = row?.meta || {};

  // Flexible support for old + new backend shapes
  const days = Number(
    meta.daysActive ??
      meta.daysActive60d ??
      meta.daysActive30d ??
      meta.daysActive7d ??
      0
  );

  const pings = Number(
    meta.pings ??
      meta.pings60d ??
      meta.pings30d ??
      meta.pings7d ??
      0
  );

  const lastPingAt = meta.lastPingAt ? new Date(meta.lastPingAt) : null;

  return (
    <div className="pgd-page" role="main" aria-label="Pi Guardian Detail">
      {/* ✅ Conflict-safe topbar */}
      <div className="pgd-topbar" aria-label="Header">
        <img
          src={backIcon}
          alt="Back"
          className="pgd-topbar-back"
          onClick={() => navigate(-1)}
        />
        <div className="pgd-topbar-title">Pi Guardian</div>
      </div>

      <div className="pgd-scroll" id="pgd-scroll">
        {/* Identity */}

<div className="pgd-user">
  <img className="pgd-avatar" src={user.profilePic} alt={user.username} />
  <div className="pgd-user-meta">
    <div className="pgd-username">@{user.username}</div>
    <div className="pgd-name">{user.name}</div>
  </div>
  <button
    type="button"
    className="pgd-view-profile"
    onClick={() => navigate(`/user/${encodeURIComponent(user.username)}`)}
  >
    View profile
  </button>
</div>

        {/* Status card */}
        <div className="pgd-card">
          {loading ? (
            <div className="pgd-loading">Loading…</div>
          ) : err ? (
            <div className="pgd-error">{String(err)}</div>
          ) : !row ? (
            <div className="pgd-empty">
              This user is not currently in the top Pi Guardians leaderboard.
            </div>
          ) : (
            <>
              <div className="pgd-window">{windowLabel(windowParam)}</div>

              <div className="pgd-card-row">
                <div className="pgd-k">Active days</div>
                <div className="pgd-v">{days}</div>
              </div>

              <div className="pgd-card-row">
                <div className="pgd-k">Total pings</div>
                <div className="pgd-v">{pings}</div>
              </div>

              {lastPingAt && (
                <div className="pgd-card-row">
                  <div className="pgd-k">Last ping</div>
                  <div className="pgd-v">{lastPingAt.toLocaleString()}</div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="pgd-footnote">
          Guardians are ranked by <strong>active days</strong> in the selected window, then by{' '}
          <strong>total pings</strong>, then by <strong>most recent ping</strong>. Pings are lightweight “I’m active”
          updates sent by the app.
        </div>
      </div>
    </div>
  );
}
